import { useEffect } from "react";
import dynamic from "next/dynamic";
import type { AppProps } from "next/app";
import { useRouter } from "next/router";
import LoginModal from "@/components/LoginModal";
import ProjectDeletionModal from "@/components/account/ProjectDeletionModal";
import AuthProvider from "@/contexts/auth/AuthContext";
import EntitiesProvider from "@/contexts/entities/EntitiesContext";
import NavigatorProvider from "@/contexts/navigator/NavigatorContext";
import ProjectsProvider from "@/contexts/projects/ProjectsContext";
import SavedResourcesProvider from "@/contexts/savedResources/SavedResourcesContext";
import Analytics from "@/lib/analytics";
import { getAllLearnerOutcomes } from "@/lib/api";
import { init as initFirebase } from "@/lib/firebase";
import { ApiLearnerOutcome } from "@/lib/models";
import "@/styles/globals.css";
import { roobert, teodor } from "@/styles/font";

const GoogleAnalytics = dynamic(
  () => import("@/lib/static/scripts").then((module) => module.GoogleAnalytics),
  { ssr: false },
);
const Hotjar = dynamic(
  () => import("@/lib/static/scripts").then((module) => module.Hotjar),
  { ssr: false },
);
const Mixpanel = dynamic(
  () => import("@/lib/static/scripts").then((module) => module.Mixpanel),
  { ssr: false },
);
const Facebook = dynamic(
  () => import("@/lib/static/scripts").then((module) => module.FacebookPixel),
  { ssr: false },
);

const CookieYesProd = dynamic(
  () => import("@/lib/static/scripts").then((module) => module.CookieYesProd),
  { ssr: false },
);

const CookieYesStaging = dynamic(
  () =>
    import("@/lib/static/scripts").then((module) => module.CookieYesStaging),
  { ssr: false },
);

initFirebase();

type NavigatorProps = {
  learnerOutcomes: ApiLearnerOutcome[];
};

export default function App({
  Component,
  pageProps,
  learnerOutcomes,
}: AppProps & NavigatorProps) {
  const isProductionFirebaseEnv =
    process.env.NEXT_PUBLIC_FIREBASE_ENV === "prod";

  const isStagingFirebaseEnv = process.env.NEXT_PUBLIC_FIREBASE_ENV === "stage";

  const router = useRouter();
  useEffect(() => {
    const trackPageView = (url: string) => {
      try {
        const queryParams = new URLSearchParams(url.split("?").pop());
        const selectionId = queryParams.get("selection");
        if (selectionId) {
          Analytics.handleNavigationPageView(learnerOutcomes, selectionId);
        } else {
          Analytics.handlePageView();
        }
      } catch (e) {
        console.error(e);
      }
    };

    router.events.on("routeChangeComplete", trackPageView);
    return () => {
      router.events.off("routeChangeComplete", trackPageView);
    };
  }, [router.events, learnerOutcomes]);

  return (
    <>
      <style jsx global>{`
        :root {
          --font-roobert: ${roobert.style.fontFamily};
          --font-teodor: ${teodor.style.fontFamily};
        }
      `}</style>
      <Mixpanel />
      {isProductionFirebaseEnv && (
        <>
          <CookieYesProd />
          <Hotjar />
          <GoogleAnalytics />
          <Facebook />
        </>
      )}
      {isStagingFirebaseEnv && <CookieYesStaging />}
      <EntitiesProvider learnerOutcomes={learnerOutcomes}>
        <AuthProvider>
          <NavigatorProvider>
            <SavedResourcesProvider>
              <ProjectsProvider>
                <Component {...pageProps} />
                <LoginModal />
                <ProjectDeletionModal />
              </ProjectsProvider>
            </SavedResourcesProvider>
          </NavigatorProvider>
        </AuthProvider>
      </EntitiesProvider>
    </>
  );
}

App.getInitialProps = async (): Promise<NavigatorProps> => {
  const isBrowser =
    typeof window !== "undefined" && typeof window.document !== "undefined";
  if (isBrowser) {
    return window.__NEXT_DATA__.props as NavigatorProps;
  }

  const learnerOutcomes = await getAllLearnerOutcomes();
  return { learnerOutcomes };
};
